/**
 * TanStack Query hooks for `android-app-links`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	patch,
	remove,
	androidAppLinksQueryKeys,
	type AndroidAppLinkView,
	type CreateAndroidAppLinksBody,
	type ListAndroidAppLinksResponse,
	type PatchAndroidAppLinksBody,
} from "./api";

export function useAndroidAppLinksList(
	host?: string,
): UseQueryResult<ListAndroidAppLinksResponse, Error> {
	return useQuery({
		queryKey: androidAppLinksQueryKeys.list(host),
		queryFn: () => list(host),
	});
}

export function useAndroidAppLinksCreate(): UseMutationResult<
	AndroidAppLinkView,
	Error,
	CreateAndroidAppLinksBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: androidAppLinksQueryKeys.all });
		},
	});
}

export function useAndroidAppLinksPatch(): UseMutationResult<
	AndroidAppLinkView,
	Error,
	{ id: string; body: PatchAndroidAppLinksBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }) => patch(id, body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: androidAppLinksQueryKeys.all });
		},
	});
}

export function useAndroidAppLinksDelete(): UseMutationResult<
	{ ok: true; id: string },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: androidAppLinksQueryKeys.all });
		},
	});
}
